import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import * as api from 'api/api'
import { DisplayStatus, PortfolioReport, Project } from 'api/types'
import { useRegion } from 'components/app-shell/context/RegionContext'
import { Button, Card, EmptyState, ErrorState, IconButton, Spinner, StatusBadge } from 'components/ui'
import {
  STATUS_LABELS,
  STATUS_ORDER,
  WEEK_DAYS,
  addDays,
  formatDate,
  monthBounds,
  monthKey,
  monthTitle,
  monthsBetween,
  parseIso,
  weekStart,
} from 'lib/format'
import { t } from 'lib/i18n'
import { useAsync } from 'lib/hooks'

const VISIBLE_ROWS = 3

const weekdayFromDate = (iso: string) => WEEK_DAYS[(parseIso(iso).getUTCDay() + 1) % 7]

const clampMonth = (key: string, months: string[]) => (months.includes(key) ? key : months[months.length - 1])

const scopeCopy = (projects: Project[]) => {
  const roles = new Set(projects.map((project) => project.myRole))
  if (roles.size === 1 && roles.has('ADMIN')) return t('كل المشاريع', 'All projects')
  if (roles.size === 1 && roles.has('PROJECT_MANAGER')) return t('المشاريع التي تديرها', 'Projects you manage')
  if (roles.size === 1 && roles.has('SITE_ENGINEER')) return t('مشاريعك', 'Your projects')
  if (roles.size === 1 && roles.has('SUPERVISOR')) {
    return t('المشاريع التي تشرف عليها · التقارير المعتمدة فقط', 'Projects you supervise · approved reports only')
  }
  if (roles.has('SUPERVISOR')) {
    return t('كل المشاريع المتاحة لك · مشاريع الإشراف تعرض التقارير المعتمدة فقط', 'Every project you can open · supervised projects show approved reports only')
  }
  return t('كل المشاريع المتاحة لك', 'Every project you can open')
}

const PortfolioGrid = ({
  reports,
  month,
  today,
  selected,
  onSelect,
}: {
  reports: PortfolioReport[]
  month: string
  today: string
  selected: string | null
  onSelect: (day: string) => void
}) => {
  const byDate = useMemo(() => {
    const grouped = new Map<string, PortfolioReport[]>()
    reports.forEach((report) => {
      const list = grouped.get(report.reportDate) ?? []
      list.push(report)
      grouped.set(report.reportDate, list)
    })
    return grouped
  }, [reports])
  const { from, to } = monthBounds(month)
  const days: string[] = []
  for (let day = weekStart(from); day <= to || days.length % 7 !== 0; day = addDays(day, 1)) days.push(day)

  return (
    <div className="calendar" role="grid" aria-label={`${t('تقويم كل المشاريع', 'Calendar for all projects')} ${monthTitle(month)}`}>
      <div className="calendar__row calendar__row--head" role="row">
        {WEEK_DAYS.map((day, index) => (
          <div key={index} role="columnheader" className={index === 6 ? 'is-weekend' : undefined}>
            {day}
          </div>
        ))}
      </div>
      {Array.from({ length: days.length / 7 }, (_, week) => (
        <div className="calendar__row" role="row" key={week}>
          {days.slice(week * 7, week * 7 + 7).map((day) => {
            const inMonth = monthKey(day) === month
            if (!inMonth) {
              return <div key={day} role="gridcell" className="calendar__cell is-blank" />
            }
            const rows = byDate.get(day) ?? []
            const shown = rows.slice(0, VISIBLE_ROWS)
            const extra = rows.length - shown.length
            const isSelected = selected === day
            return (
              <div key={day} role="gridcell" className="calendar__cellwrap">
                <button
                  type="button"
                  className={`calendar__cell calendar__cell--portfolio${day === today ? ' is-today' : ''}${isSelected ? ' is-selected' : ''}`}
                  aria-pressed={isSelected}
                  aria-label={`${weekdayFromDate(day)} ${formatDate(day)} · ${rows.length}`}
                  onClick={() => onSelect(day)}
                >
                  <span className="calendar__day">{parseIso(day).getUTCDate()}</span>
                  {shown.map((report) => (
                    <span key={report.id} className="calendar__project">
                      <span className={`calendar__dot calendar__dot--${report.status.toLowerCase()}`} />
                      <span className="calendar__project-name">{report.projectName}</span>
                    </span>
                  ))}
                  {extra > 0 ? <span className="calendar__more">+{extra}</span> : null}
                </button>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export const PortfolioCalendarPage = () => {
  const { locale } = useRegion()
  const [params, setParams] = useSearchParams()
  const { data: projects, error: projectsError, loading: projectsLoading, reload: reloadProjects } = useAsync(api.fetchProjects, [])
  const { data: today } = useAsync(api.fetchToday, [])

  const months = useMemo(() => {
    if (!projects?.length) return [monthKey(today ?? new Date().toISOString().slice(0, 10))]
    const start = projects.reduce((min, project) => (project.startDate < min ? project.startDate : min), projects[0].startDate)
    const end = projects.reduce((max, project) => (project.endDate > max ? project.endDate : max), projects[0].endDate)
    const list = monthsBetween(start, end)
    const current = monthKey(today ?? start)
    if (!list.includes(current)) list.push(current)
    return list.sort()
  }, [projects, today, locale])

  const month = clampMonth(params.get('month') ?? (today ? monthKey(today) : months[0]), months)
  const statusFilter = (params.get('status') as DisplayStatus | null) ?? null
  const selected = params.get('day')
  const { from, to } = monthBounds(month)
  const { data: reports, error, loading, reload } = useAsync(() => api.fetchPortfolioReports(from, to), [from, to])

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    Object.entries(patch).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)))
    setParams(next, { replace: true })
  }

  const counts = useMemo(() => {
    const result = Object.fromEntries(STATUS_ORDER.map((status) => [status, 0])) as Record<DisplayStatus, number>
    reports?.forEach((report) => (result[report.status] += 1))
    return result
  }, [reports])

  const visible = useMemo(() => {
    return (reports ?? []).filter((report) => !statusFilter || report.status === statusFilter)
  }, [reports, statusFilter])

  const selectedReports = visible.filter((report) => report.reportDate === selected)
  const monthIndex = months.indexOf(month)
  const scope = projects?.length ? scopeCopy(projects) : ''

  const openToday = () => {
    if (!today) return
    update({ month: clampMonth(monthKey(today), months), day: today })
  }

  if (projectsLoading && !projects) return <Spinner />
  if (projectsError) return <ErrorState message={projectsError} onRetry={reloadProjects} />
  if (projects && !projects.length) {
    return (
      <div className="page">
        <h1>{t('التقويم', 'Calendar')}</h1>
        <EmptyState icon="calendar" title={t('لا توجد مشاريع لعرضها', 'No projects to show')} />
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('التقويم', 'Calendar')}</h1>
          <p className="muted">{scope}</p>
        </div>
        <div className="toolbar">
          <div className="month-switch" role="group" aria-label={t('اختيار الشهر', 'Choose month')}>
            <IconButton
              icon="chevronRight"
              label={t('الشهر السابق', 'Previous month')}
              disabled={monthIndex <= 0}
              onClick={() => update({ month: months[monthIndex - 1], day: null })}
            />
            <label className="sr-only" htmlFor="portfolio-month">
              {t('الشهر', 'Month')}
            </label>
            <select
              id="portfolio-month"
              className="month-switch__select"
              value={month}
              onChange={(event) => update({ month: event.target.value, day: null })}
            >
              {months.map((key) => (
                <option key={key} value={key}>
                  {monthTitle(key)}
                </option>
              ))}
            </select>
            <IconButton
              icon="chevronLeft"
              label={t('الشهر التالي', 'Next month')}
              disabled={monthIndex >= months.length - 1}
              onClick={() => update({ month: months[monthIndex + 1], day: null })}
            />
          </div>
          <Button onClick={openToday} disabled={!today}>
            {t('اليوم', 'Today')}
          </Button>
        </div>
      </div>

      <div className="stats" role="group" aria-label={t('تصفية حسب الحالة', 'Filter by status')}>
        <button
          type="button"
          className={statusFilter ? 'stat' : 'stat is-active'}
          aria-pressed={!statusFilter}
          onClick={() => update({ status: null })}
        >
          <span className="stat__value">{reports?.length ?? '—'}</span>
          <span className="stat__label">{t('كل التقارير', 'All reports')}</span>
        </button>
        {STATUS_ORDER.map((status) => (
          <button
            key={status}
            type="button"
            className={`stat stat--${status.toLowerCase()}${statusFilter === status ? ' is-active' : ''}`}
            aria-pressed={statusFilter === status}
            onClick={() => update({ status: statusFilter === status ? null : status })}
          >
            <span className="stat__value">{reports ? counts[status] : '—'}</span>
            <span className="stat__label">{STATUS_LABELS[status]}</span>
          </button>
        ))}
      </div>

      <ul className="calendar-legend">
        {STATUS_ORDER.map((status) => (
          <li key={status}>
            <span className={`calendar__dot calendar__dot--${status.toLowerCase()}`} />
            {STATUS_LABELS[status]}
          </li>
        ))}
      </ul>

      <Card title={monthTitle(month)}>
        {loading && !reports ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : (
          <PortfolioGrid
            reports={visible}
            month={month}
            today={today ?? ''}
            selected={selected}
            onSelect={(day) => update({ day: selected === day ? null : day })}
          />
        )}
      </Card>

      {selected && monthKey(selected) === month ? (
        <Card title={`${weekdayFromDate(selected)} · ${formatDate(selected)}`}>
          {selectedReports.length ? (
            <ul className="calendar-day">
              {selectedReports.map((report) => (
                <li key={report.id}>
                  <Link to={`/projects/${report.projectId}/reports/${report.id}`}>
                    <span className={`calendar__dot calendar__dot--${report.status.toLowerCase()}`} />
                    <span className="calendar-day__project">{report.projectName}</span>
                    <span className="calendar__code">{report.code}</span>
                    <StatusBadge status={report.status} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="calendar" title={t('لا تقارير في هذا اليوم', 'No reports on this day')} />
          )}
        </Card>
      ) : null}
    </div>
  )
}
